// pages/Notes.jsx
// Halaman Catatan / Memo bersama tim — dipindahkan dari tab "Notes" di
// TableMaintenance menjadi halaman mandiri di sidebar.
// Self-contained: bawa sendiri MarkdownView + mdInline + UI kecil + useApi,
// supaya tidak bergantung pada TableMaintenance.
//
// Pakai axios instance + token dari AuthContext (otomatis via interceptor).

import { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import useTabDeepLink from '../hooks/useTabDeepLink';
import { api as axiosApi, useAuth } from '../context/AuthContext';
import { useLang } from '../context/LanguageContext';
import useIsMobile from '../hooks/useIsMobile';
import { downloadNotesZip } from '../utils/notesExport';
import ImportNotesZipModal from '../components/ImportNotesZipModal';

// ── useApi — pakai axios instance dari AuthContext (token otomatis via interceptor) ──
function useApi() {
  return useCallback(async (path, opts = {}) => {
    const method = (opts.method || 'GET').toLowerCase();
    const body   = opts.body ? JSON.parse(opts.body) : undefined;
    try {
      const res = method === 'get' || method === 'delete'
        ? await axiosApi[method](path)
        : await axiosApi[method](path, body);
      return res.data;
    } catch (err) {
      throw new Error(err.response?.data?.error || err.message || 'API error');
    }
  }, []);
}

// ── Shared UI kecil (di-inline supaya halaman berdiri sendiri) ───────────────
function Alert({ msg, type = 'info', onClose }) {
  if (!msg) return null;
  const c = {
    success: { bg: '#d1fae5', color: '#065f46', border: '#6ee7b7' },
    error:   { bg: '#fee2e2', color: '#7f1d1d', border: '#fca5a5' },
    info:    { bg: 'rgb(var(--brand-100))', color: '#1e3a5f', border: '#93c5fd' },
  }[type] || {};
  return (
    <div style={{ background: c.bg, color: c.color, border: `1px solid ${c.border}`, borderRadius: 8, padding: '10px 14px', marginBottom: 12, fontSize: 13, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <span>{msg}</span>
      {onClose && <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: c.color, fontSize: 18, lineHeight: 1, marginLeft: 8 }}>×</button>}
    </div>
  );
}

function Btn({ children, onClick, variant = 'default', size = 'md', disabled = false }) {
  const base = { display: 'inline-flex', alignItems: 'center', gap: 5, border: '1px solid', borderRadius: 8, cursor: disabled ? 'not-allowed' : 'pointer', fontFamily: 'inherit', fontSize: size === 'sm' ? 12 : 13, padding: size === 'sm' ? '5px 10px' : '8px 14px', opacity: disabled ? 0.55 : 1, transition: 'all 0.15s', fontWeight: 500 };
  const variants = {
    default: { background: '#fff',    borderColor: '#e2e8f0', color: '#475569' },
    primary: { background: 'rgb(var(--brand-600))', borderColor: 'rgb(var(--brand-600))', color: '#fff' },
    danger:  { background: '#fef2f2', borderColor: '#fecaca', color: '#dc2626' },
  };
  return <button onClick={disabled ? undefined : onClick} style={{ ...base, ...variants[variant] }}>{children}</button>;
}

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <label style={{ display: 'block', fontSize: 12, color: '#64748b', marginBottom: 4, fontWeight: 600 }}>{label}</label>
      {children}
    </div>
  );
}

const inputStyle = { width: '100%', padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, fontFamily: 'inherit', boxSizing: 'border-box', outline: 'none' };
function Input(props) { return <input {...props} style={inputStyle} />; }

function Loading() {
  return (
    <div style={{ padding: '3rem', textAlign: 'center', color: '#94a3b8', fontSize: 14 }}>
      <div style={{ fontSize: 28, marginBottom: 8 }}>⏳</div>
      Memuat data…
    </div>
  );
}
function Empty({ icon, text }) {
  return (
    <div style={{ padding: '3rem', textAlign: 'center', color: '#cbd5e1' }}>
      <div style={{ fontSize: 40, marginBottom: 8 }}>{icon}</div>
      <div style={{ fontSize: 14 }}>{text}</div>
    </div>
  );
}

// ── Markdown ringan (tanpa dependency) — render heading, bold, italic, code,
// list, blockquote, hr, link, tabel. Aman: pakai elemen React, bukan innerHTML.

// Palet code block — tema terang, senada dengan sisa dashboard.
const CB = {
  bg: '#f8fafc', border: '#e2e8f0', label: '#94a3b8', text: '#334155',
  cmd: '#059669', flag: '#0284c7', str: '#be123c', comment: '#94a3b8',
};

// Bahasa yang diwarnai. Selain ini dirender polos — lebih baik tanpa warna
// daripada warna yang salah.
const SHELLS = new Set(['bash', 'sh', 'shell', 'zsh', 'console', 'terminal']);

// Highlighter shell seadanya. Sengaja TIDAK memakai innerHTML: setiap token
// jadi elemen React, jadi isi catatan tidak bisa menyuntikkan markup.
// Cakupannya sempit dan itu disengaja — komentar, string, flag, dan kata di
// posisi perintah. Selebihnya dibiarkan polos.
function highlightShell(code) {
  // Token: string kutip (3 gaya) | komentar | spasi | sisanya
  // Operator dipisah jadi token sendiri, supaya kata setelah `;` atau `|`
  // ikut dikenali sebagai posisi perintah (mis. `do` pada `...; do`).
  const RE = /("(?:[^"\\]|\\.)*"|'(?:[^'\\]|\\.)*'|`(?:[^`\\]|\\.)*`|#[^\n]*|\s+|&&|\|\||[;|()]|[^\s;|()&]+)/g;
  const out = [];
  let k = 0;

  code.split('\n').forEach((line, li) => {
    if (li > 0) out.push('\n');
    // Kata pertama sebuah perintah: awal baris, atau tepat setelah | && || ; (
    let expectCmd = true;
    const toks = line.match(RE) || [];
    for (const tok of toks) {
      const c = tok[0];
      if (/^\s+$/.test(tok)) { out.push(tok); continue; }

      let color = null;
      if (c === '#') color = CB.comment;
      else if (c === '"' || c === "'" || c === '`') { color = CB.str; expectCmd = false; }
      else if (/^-{1,2}[A-Za-z0-9]/.test(tok)) { color = CB.flag; expectCmd = false; }
      else if (/^(\||&&|\|\||;|\()$/.test(tok)) { expectCmd = true; }
      else if (expectCmd) { color = CB.cmd; expectCmd = false; }
      else expectCmd = false;

      out.push(color
        ? <span key={`h${k++}`} style={{ color, ...(c === '#' ? { fontStyle: 'italic' } : null) }}>{tok}</span>
        : tok);
    }
  });
  return out;
}

function CodeBlock({ lang, code }) {
  const [copied, setCopied] = useState(false);
  const [hovered, setHovered] = useState(false);

  const copy = () => {
    navigator.clipboard.writeText(code).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => {
      // fallback
      const ta = document.createElement('textarea');
      ta.value = code;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const shell = lang && SHELLS.has(String(lang).toLowerCase());

  return (
    <div
      style={{
        position: 'relative', margin: '8px 0',
        background: CB.bg, border: `1px solid ${CB.border}`, borderRadius: 8,
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {lang && (
        <div style={{
          fontSize: 10.5, color: CB.label, fontFamily: 'monospace',
          userSelect: 'none', padding: '8px 14px 0',
        }}>
          {lang}
        </div>
      )}
      <button
        onClick={copy}
        style={{
          position: 'absolute', top: 6, right: 6,
          background: copied ? '#16a34a' : '#ffffff',
          border: `1px solid ${copied ? '#16a34a' : CB.border}`,
          borderRadius: 6, padding: '3px 9px',
          color: copied ? '#fff' : '#64748b',
          fontSize: 11, fontFamily: 'inherit', cursor: 'pointer',
          transition: 'all 0.15s', opacity: hovered || copied ? 1 : 0,
        }}
      >
        {copied ? '✓ Copied' : 'Copy'}
      </button>
      <pre style={{
        background: 'transparent', color: CB.text, borderRadius: 0,
        padding: `${lang ? '4px' : '12px'} 14px 12px`,
        fontSize: 12.5, fontFamily: 'monospace', overflowX: 'auto',
        lineHeight: 1.5, margin: 0,
      }}>
        {shell ? highlightShell(code) : code}
      </pre>
    </div>
  );
}

function mdInline(text, keyPrefix, hl) {
  const nodes = [];
  let rest = text;
  let i = 0;
  const re = /(`[^`]+`)|(\*\*[^*]+\*\*)|(\*[^*]+\*)|(_[^_]+_)|(\[[^\]]+\]\([^)]+\))/;

  // Pecah teks biasa dengan highlight marker
  const pushText = (t, k) => {
    if (!hl || !t) { if (t) nodes.push(t); return; }
    const lower = t.toLowerCase();
    const nl    = hl.toLowerCase();
    let pos = 0, ki = 0;
    while (pos < t.length) {
      const idx = lower.indexOf(nl, pos);
      if (idx === -1) { nodes.push(t.slice(pos)); break; }
      if (idx > pos) nodes.push(t.slice(pos, idx));
      nodes.push(
        <mark key={`${k}-h${ki++}`} data-pvhl="true"
          style={{ background: '#fef08a', color: '#0f172a', borderRadius: 2, padding: '0 1px' }}>
          {t.slice(idx, idx + hl.length)}
        </mark>
      );
      pos = idx + hl.length;
    }
  };

  while (rest.length) {
    const m = rest.match(re);
    if (!m) { pushText(rest, `${keyPrefix}-${i++}`); break; }
    if (m.index > 0) pushText(rest.slice(0, m.index), `${keyPrefix}-${i++}`);
    const tok = m[0];
    const k = `${keyPrefix}-${i++}`;
    if (tok.startsWith('`')) {
      nodes.push(<code key={k} style={{ background: '#f1f5f9', padding: '1px 5px', borderRadius: 4, fontFamily: 'monospace', fontSize: '0.92em', color: '#be123c' }}>{tok.slice(1, -1)}</code>);
    } else if (tok.startsWith('**')) {
      nodes.push(<strong key={k} style={{ fontWeight: 700, color: '#0f172a' }}>{mdInline(tok.slice(2, -2), k, hl)}</strong>);
    } else if (tok.startsWith('*')) {
      nodes.push(<em key={k}>{mdInline(tok.slice(1, -1), k, hl)}</em>);
    } else if (tok.startsWith('_')) {
      nodes.push(<em key={k}>{mdInline(tok.slice(1, -1), k, hl)}</em>);
    } else {
      const lm = tok.match(/\[([^\]]+)\]\(([^)]+)\)/);
      if (lm) nodes.push(<a key={k} href={lm[2]} target="_blank" rel="noopener noreferrer" style={{ color: 'rgb(var(--brand-600))', textDecoration: 'underline' }}>{lm[1]}</a>);
      else pushText(tok, k);
    }
    rest = rest.slice(m.index + tok.length);
  }
  return nodes;
}

export function MarkdownView({ source, highlight }) {
  const lines = String(source || '').replace(/\r\n/g, '\n').split('\n');
  const blocks = [];
  let i = 0;
  let bi = 0;

  while (i < lines.length) {
    const line = lines[i];

    // Code block ```
    if (/^```/.test(line.trim())) {
      const lang = line.trim().replace(/^```/, '').trim().toLowerCase();
      const buf = [];
      i++;
      while (i < lines.length && !/^```/.test(lines[i].trim())) { buf.push(lines[i]); i++; }
      i++; // skip closing ```
      const rawContent = buf.join('\n');
      // Jika lang = svg/html atau isi dimulai dengan <svg / <div / <html → render sebagai HTML
      const isHtmlContent = ['svg', 'html'].includes(lang) ||
        /^\s*<(svg|div|html|figure)\b/i.test(rawContent.trimStart());
      if (isHtmlContent) {
        blocks.push(
          <div key={`b${bi++}`} style={{ margin: '8px 0', overflowX: 'auto', maxWidth: '100%' }}>
            <style>{`
              .md-html-block svg { max-width: 720px !important; width: 100% !important; height: auto !important; }
              .md-html-block img { max-width: 100%; height: auto; }
            `}</style>
            <div className="md-html-block" dangerouslySetInnerHTML={{ __html: rawContent }} />
          </div>
        );
      } else {
        blocks.push(
          <CodeBlock key={`b${bi++}`} lang={lang} code={rawContent} />
        );
      }
      continue;
    }

    // Raw HTML / SVG block — render dengan dangerouslySetInnerHTML
    if (/^<[a-zA-Z!\/]/.test(line.trim())) {
      const tagMatch = line.trim().match(/^<([a-zA-Z][a-zA-Z0-9]*)/);
      const openTag  = tagMatch ? tagMatch[1].toLowerCase() : null;
      const buf = [line];
      i++;
      // Untuk tag blok (div, svg, dll): kumpulkan sampai closing tag — abaikan blank line di dalam
      const blockTags = ['div', 'svg', 'figure', 'section', 'article', 'details', 'summary'];
      if (openTag && blockTags.includes(openTag)) {
        const closeTag = `</${openTag}`;
        while (i < lines.length) {
          buf.push(lines[i]);
          if (lines[i].toLowerCase().includes(closeTag)) { i++; break; }
          i++;
        }
      } else {
        // Tag pendek: hentikan di baris kosong
        while (i < lines.length && lines[i].trim() !== '') { buf.push(lines[i]); i++; }
      }
      blocks.push(
        <div key={`b${bi++}`} style={{ margin: '8px 0', overflowX: 'auto', maxWidth: '100%' }}>
          <div className="md-html-block" dangerouslySetInnerHTML={{ __html: buf.join('\n') }} />
        </div>
      );
      continue;
    }

    // Horizontal rule
    if (/^(-{3,}|\*{3,}|_{3,})\s*$/.test(line.trim())) {
      blocks.push(<hr key={`b${bi++}`} style={{ border: 0, borderTop: '1px solid #e2e8f0', margin: '14px 0' }} />);
      i++; continue;
    }

    // Heading
    const hm = line.match(/^(#{1,6})\s+(.*)$/);
    if (hm) {
      const level = hm[1].length;
      const sizes = { 1: 20, 2: 17, 3: 15, 4: 14, 5: 13, 6: 12.5 };
      const Tag = `h${Math.min(level, 6)}`;
      blocks.push(
        <Tag key={`b${bi++}`} style={{ fontSize: sizes[level] || 14, fontWeight: 700, color: '#0f172a', margin: level <= 2 ? '16px 0 8px' : '12px 0 6px', lineHeight: 1.3 }}>
          {mdInline(hm[2], `h${bi}`, highlight)}
        </Tag>
      );
      i++; continue;
    }

    // Table (GFM pipe table)
    const isDivider = (s) => /^\s*\|?\s*:?-{1,}:?\s*(\|\s*:?-{1,}:?\s*)*\|?\s*$/.test(s) && s.includes('-');
    if (line.includes('|') && i + 1 < lines.length && isDivider(lines[i + 1])) {
      const splitRow = (s) => s.trim().replace(/^\|/, '').replace(/\|$/, '').split('|').map(c => c.trim());
      const headers  = splitRow(line);
      const aligns   = splitRow(lines[i + 1]).map(d => {
        const t = d.trim();
        if (t.startsWith(':') && t.endsWith(':')) return 'center';
        if (t.endsWith(':')) return 'right';
        return 'left';
      });
      i += 2;
      const rows = [];
      while (i < lines.length && lines[i].includes('|') && lines[i].trim() !== '' && !isDivider(lines[i])) {
        rows.push(splitRow(lines[i])); i++;
      }
      blocks.push(
        <div key={`b${bi++}`} style={{ overflowX: 'auto', margin: '10px 0' }}>
          <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 12.5, border: '1px solid #e2e8f0' }}>
            <thead>
              <tr style={{ background: '#f8fafc' }}>
                {headers.map((h, ci) => (
                  <th key={ci} style={{ textAlign: aligns[ci] || 'left', padding: '7px 12px', fontWeight: 700, color: '#0f172a', borderBottom: '2px solid #e2e8f0', whiteSpace: 'nowrap' }}>
                    {mdInline(h, `th${bi}-${ci}`, highlight)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, ri) => (
                <tr key={ri} style={{ background: ri % 2 ? '#f8fafc' : '#fff' }}>
                  {headers.map((_, ci) => (
                    <td key={ci} style={{ textAlign: aligns[ci] || 'left', padding: '6px 12px', color: '#334155', borderBottom: '1px solid #f1f5f9' }}>
                      {mdInline((r[ci] || ''), `td${bi}-${ri}-${ci}`, highlight)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
      continue;
    }

    // Blockquote
    if (/^>\s?/.test(line)) {
      const buf = [];
      while (i < lines.length && /^>\s?/.test(lines[i])) { buf.push(lines[i].replace(/^>\s?/, '')); i++; }
      blocks.push(
        <blockquote key={`b${bi++}`} style={{ borderLeft: '3px solid #cbd5e1', paddingLeft: 12, margin: '8px 0', color: '#475569', fontStyle: 'italic' }}>
          {buf.map((b, j) => <div key={j}>{mdInline(b, `bq${bi}-${j}`, highlight)}</div>)}
        </blockquote>
      );
      continue;
    }

    // Unordered list
    if (/^\s*[-*+]\s+/.test(line)) {
      const items = [];
      while (i < lines.length && /^\s*[-*+]\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*[-*+]\s+/, '')); i++;
      }
      blocks.push(
        <ul key={`b${bi++}`} style={{ margin: '8px 0', paddingLeft: 22, lineHeight: 1.6 }}>
          {items.map((it, j) => <li key={j} style={{ marginBottom: 3 }}>{mdInline(it, `ul${bi}-${j}`, highlight)}</li>)}
        </ul>
      );
      continue;
    }

    // Ordered list
    if (/^\s*\d+\.\s+/.test(line)) {
      const items = [];
      while (i < lines.length && /^\s*\d+\.\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*\d+\.\s+/, '')); i++;
      }
      blocks.push(
        <ol key={`b${bi++}`} style={{ margin: '8px 0', paddingLeft: 24, lineHeight: 1.6 }}>
          {items.map((it, j) => <li key={j} style={{ marginBottom: 3 }}>{mdInline(it, `ol${bi}-${j}`, highlight)}</li>)}
        </ol>
      );
      continue;
    }

    // Baris kosong → skip
    if (line.trim() === '') { i++; continue; }

    // Paragraf
    const para = [];
    while (i < lines.length && lines[i].trim() !== '' &&
           !/^```/.test(lines[i].trim()) && !/^#{1,6}\s/.test(lines[i]) &&
           !/^>\s?/.test(lines[i]) && !/^\s*[-*+]\s+/.test(lines[i]) &&
           !/^\s*\d+\.\s+/.test(lines[i]) && !/^(-{3,}|\*{3,}|_{3,})\s*$/.test(lines[i].trim()) &&
           !(lines[i].includes('|') && i + 1 < lines.length && isDivider(lines[i + 1]))) {
      para.push(lines[i]); i++;
    }
    blocks.push(
      <p key={`b${bi++}`} style={{ margin: '6px 0', lineHeight: 1.6, color: '#334155', wordBreak: 'break-word' }}>
        {para.map((pl, j) => <span key={j}>{mdInline(pl, `p${bi}-${j}`, highlight)}{j < para.length - 1 ? <br /> : null}</span>)}
      </p>
    );
  }

  return <div style={{ fontSize: 13 }}>{blocks}</div>;
}


// ── ShareModal — pilih expiry lalu tampilkan URL share ──────────────────────
const EXPIRY_OPTS = [
  { value: '7',  labelId: '7 hari',   labelEn: '7 days'   },
  { value: '30', labelId: '30 hari',  labelEn: '30 days'  },
  { value: '90', labelId: '90 hari',  labelEn: '90 days'  },
  { value: '',   labelId: 'Permanen', labelEn: 'Permanent' },
];

function ShareModal({ note, id, onClose, api }) {
  const {
    t
  } = useLang();

  const [step,    setStep]    = useState('pick');   // 'pick' | 'done'
  const [expiry,  setExpiry]  = useState('7');
  const [url,     setUrl]     = useState('');
  const [busy,    setBusy]    = useState(false);
  const [err,     setErr]     = useState('');
  const [copied,  setCopied]  = useState(false);
  const overlayRef = useRef(null);

  const create = async () => {
    setBusy(true); setErr('');
    try {
      const body = {
        dashboardKey: 'note',
        label: note.title || (t('notes.untitled')),
        params: { noteId: note.id },
      };
      if (expiry) body.expiresInDays = Number(expiry);
      const data = await api('/api/share', { method: 'POST', body: JSON.stringify(body) });
      setUrl(data.url.replace(/^http:\/\//i, 'https://'));
      setStep('done');
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  const copy = async () => {
    try { await navigator.clipboard.writeText(url); setCopied(true); setTimeout(() => setCopied(false), 1800); }
    catch (_) {}
  };

  return (
    <div
      ref={overlayRef}
      onClick={e => { if (e.target === overlayRef.current) onClose(); }}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
    >
      <div style={{ background: '#fff', borderRadius: 14, padding: '20px 22px', width: '100%', maxWidth: 420, boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <div style={{ fontWeight: 700, fontSize: 14, color: '#0f172a' }}>
            🔗 {t('notes.shareNote')}
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: '#94a3b8', lineHeight: 1 }}>×</button>
        </div>

        {/* Judul note */}
        <div style={{ fontSize: 12.5, color: '#475569', background: '#f8fafc', borderRadius: 8, padding: '7px 10px', marginBottom: 14, border: '1px solid #e2e8f0', fontStyle: 'italic' }}>
          "{note.title || (t('notes.untitled'))}"
        </div>

        {step === 'pick' ? (
          <>
            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontSize: 12, color: '#64748b', marginBottom: 6, fontWeight: 600 }}>
                {t('notes.linkExpiry')}
              </label>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {EXPIRY_OPTS.map(o => (
                  <button
                    key={o.value}
                    onClick={() => setExpiry(o.value)}
                    style={{
                      flex: '1 0 auto', padding: '7px 10px', borderRadius: 8, cursor: 'pointer',
                      fontFamily: 'inherit', fontSize: 12.5, fontWeight: 600, border: '1px solid',
                      borderColor: expiry === o.value ? 'rgb(var(--brand-600))' : '#e2e8f0',
                      background: expiry === o.value ? 'rgb(var(--brand-50))' : '#f8fafc',
                      color: expiry === o.value ? 'rgb(var(--brand-700))' : '#64748b',
                    }}
                  >
                    {id ? o.labelId : o.labelEn}
                  </button>
                ))}
              </div>
            </div>
            {err && <div style={{ fontSize: 12, color: '#dc2626', marginBottom: 10 }}>{err}</div>}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <Btn onClick={onClose}>{t('notes.cancel')}</Btn>
              <Btn variant="primary" disabled={busy} onClick={create}>
                {busy ? (t('notes.creating')) : (t('notes.createLink'))}
              </Btn>
            </div>
          </>
        ) : (
          <>
            <div style={{ fontSize: 12, color: '#64748b', marginBottom: 6, fontWeight: 600 }}>
              {t('notes.linkReadyShare')}
            </div>
            <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
              <input
                readOnly value={url} onFocus={e => e.target.select()}
                style={{ flex: 1, minWidth: 0, padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 11.5, fontFamily: 'monospace', background: '#f8fafc', color: '#334155', outline: 'none' }}
              />
              <Btn onClick={copy}>
                {copied ? (t('notes.copied')) : (t('notes.copy'))}
              </Btn>
            </div>
            <div style={{ fontSize: 11.5, color: '#94a3b8', marginBottom: 14 }}>
              {t('notes.anyoneLinkCanViewNote')}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Btn variant="primary" onClick={onClose}>{t('notes.done')}</Btn>
            </div>
          </>
        )}
      </div>
    </div>
  );
}


function NotesBoard({ api, id }) {
  const {
    lang,
    t
  } = useLang();   // dibutuhkan oleh effect resize divider (arah RTL)
  const isMobile = useIsMobile();
  const { user, hasAccess } = useAuth();
  const isAdmin  = user?.role === 'admin';
  const canPlain = hasAccess('notes_plain');   // akses halaman Notes Plain (tujuan demote)
  const [demote, setDemote] = useState(null);  // note yang akan di-demote ke Plain
  const [demoteBusy, setDemoteBusy] = useState(false);
  const [searchParams] = useSearchParams();
  const [notes, setNotes]           = useState(null);
  // -- Mass download: pilih banyak catatan -> ZIP berisi .md (1 tab = 1 file) --
  const [selMode, setSelMode]       = useState(false);
  const [selIds, setSelIds]         = useState(() => new Set());
  const [zipBusy, setZipBusy]       = useState(false);
  // -- Import ZIP berisi .md -> catatan baru (pratinjau dulu) --
  const [impFile, setImpFile]       = useState(null);
  const zipRef                      = useRef(null);
  const [selectedId, setSelectedId] = useState(null);
  const [editing, setEditing]       = useState(null);
  const [detailTab, setDetailTab]   = useState(0);   // tab aktif di mode baca
  const [busy, setBusy]             = useState(false);
  const [alert, setAlert]           = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [q, setQ]                   = useState('');
  const [shareNote, setShareNote]   = useState(null); // note yang sedang di-share
  const [tab, setTab]               = useState(() => {
    const p = searchParams.get('tab');
    return (p === 'private' || p === 'common') ? p : 'private';
  }); // 'private' | 'common'
  useTabDeepLink('notes', (p) => p === 'private' || p === 'common', setTab);
  const [leftOpen, setLeftOpen]     = useState(true);
  // ── Resizable panel kiri: lebar tersimpan di localStorage. ──
  const [leftW, setLeftW] = useState(() => {
    const v = parseInt(localStorage.getItem('notesLeftW') || '288', 10);
    return v >= 200 && v <= 600 ? v : 288;
  });
  const [dragging, setDragging] = useState(false);
  const dragRef = useRef(null); // { startX, startW }
  const startDrag = (e) => { e.preventDefault(); dragRef.current = { startX: e.clientX, startW: leftW }; setDragging(true); };
  useEffect(() => {
    if (!dragging) return;
    const rtl = lang === 'ar'; // panel kiri tampil di kanan saat RTL → arah drag dibalik
    const mm = (e) => {
      const d = dragRef.current; if (!d) return;
      const dx = e.clientX - d.startX;
      setLeftW(Math.min(600, Math.max(200, d.startW + (rtl ? -dx : dx))));
    };
    const mu = () => {
      setDragging(false); dragRef.current = null;
      setLeftW(w => { try { localStorage.setItem('notesLeftW', String(w)); } catch (_) {} return w; });
    };
    window.addEventListener('mousemove', mm);
    window.addEventListener('mouseup', mu);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    return () => {
      window.removeEventListener('mousemove', mm);
      window.removeEventListener('mouseup', mu);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [dragging, lang]);
  // ── Context menu (klik kanan di item daftar). ──
  const [ctxMenu, setCtxMenu] = useState(null); // { x, y, note }
  const [sortBy, setSortBy]         = useState('updated_desc'); // 'updated_desc'|'updated_asc'|'title_asc'|'title_desc'
  // ── Drag-reorder tab (editor & mode baca) ──
  const [editDragIdx,  setEditDragIdx]  = useState(null); // index di editing.tabs (0-based) yang sedang di-drag
  const [editOverIdx,  setEditOverIdx]  = useState(null); // index target hover saat drag
  const [viewDragIdx,  setViewDragIdx]  = useState(null); // index di selected.tabs (0-based) yang sedang di-drag
  const [viewOverIdx,  setViewOverIdx]  = useState(null);
  const [reordering,   setReordering]   = useState(false); // busy saat persist reorder mode baca
  const [pendingHl, setPendingHl]   = useState(null); // snippet untuk di-scroll/highlight
  const contentRef = useRef(null);                     // container isi note (detail)
  const textareaRef = useRef(null);                    // ref ke textarea editor

  // ── Search / Replace state ──
  const [showSR,     setShowSR]     = useState(false);
  const [srFind,     setSrFind]     = useState('');
  const [srReplace,  setSrReplace]  = useState('');
  const [srMatchIdx, setSrMatchIdx] = useState(-1);
  const [srCount,    setSrCount]    = useState(null);
  const [showHelp,   setShowHelp]   = useState(false);

  // ── Preview search state ──
  const [pvSearch,   setPvSearch]   = useState('');
  const [pvMatchIdx, setPvMatchIdx] = useState(0);
  const [pvTotal,    setPvTotal]    = useState(0);
  const [pvTabHits,  setPvTabHits]  = useState([]); // [{idx, count, label}] — tab lain (selain aktif) yg juga match
  const pvSearchRef  = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      const raw = e.detail?.noteId;
      if (raw == null) return;
      const match = (notes || []).find(n => String(n.id) === String(raw));
      if (!match) return;
      setLeftOpen(true);
      setEditing(null);
      setShowPreview(false);
      setSelectedId(match.id);
      // Loncat ke tab yang dimaksud (0 = utama, 1..n = tab). Default 0 bila tak ada.
      const tabIdx = Number.isInteger(e.detail?.tab) ? e.detail.tab : 0;
      setDetailTab(tabIdx);
      setPendingHl(e.detail?.snippet ? String(e.detail.snippet) : null); // simpan cuplikan
    };
    window.addEventListener("ai:open-note", handler);
    return () => window.removeEventListener("ai:open-note", handler);
  }, [notes]);

  // ── [Citation] Deep-link dari halaman lain: ?note=<id>&ntab=<n>&hl=<snippet>
  //    (di-navigate oleh DashboardContainer saat klik sumber AI di luar Notes).
  const deepLinkDone = useRef(false);
  useEffect(() => {
    if (deepLinkDone.current || !notes) return;
    const rawId = searchParams.get('note');
    if (rawId == null) return;
    const match = notes.find(n => String(n.id) === String(rawId));
    if (!match) return;
    deepLinkDone.current = true;
    setLeftOpen(true);
    setEditing(null);
    setShowPreview(false);
    setSelectedId(match.id);
    const t = parseInt(searchParams.get('ntab') || '0', 10);
    setDetailTab(Number.isInteger(t) && t >= 0 ? t : 0);
    const hl = searchParams.get('hl');
    if (hl) setPendingHl(hl);
  }, [notes, searchParams]);

  // Setelah note terpilih & ter-render, scroll + highlight ke bagian yang cocok.
  useEffect(() => {
    if (!pendingHl || !selectedId) return;
    const root = contentRef.current;
    if (!root) return;
    // Ambil potongan pembanding: baris pertama yg bermakna dari snippet, dinormalisasi.
    const norm = (s) => String(s).toLowerCase().replace(/[\s\u00a0]+/g, ' ').replace(/[^a-z0-9 ]/g, '').trim();
    const firstLine = String(pendingHl).split('\n').map(l => l.trim()).find(l => norm(l).length >= 8) || pendingHl;
    const needle = norm(firstLine).slice(0, 60);
    if (!needle) { setPendingHl(null); return; }

    const t = setTimeout(() => {
      // Cari elemen blok yang teksnya memuat needle (perbandingan ternormalisasi).
      const candidates = root.querySelectorAll('p, li, td, th, h1, h2, h3, h4, pre, blockquote, div');
      let target = null;
      for (const el of candidates) {
        if (el.querySelector('p, li, td, th, pre')) continue; // utamakan elemen daun
        if (norm(el.textContent || '').includes(needle)) { target = el; break; }
      }
      if (!target) { // fallback: cari elemen daun apa pun yang memuat needle
        for (const el of candidates) {
          if (norm(el.textContent || '').includes(needle)) { target = el; break; }
        }
      }
      if (target) {
        target.scrollIntoView({ behavior: 'smooth', block: 'center' });
        const prevBg = target.style.backgroundColor;
        const prevTrans = target.style.transition;
        target.style.transition = 'background-color 0.4s';
        target.style.backgroundColor = '#fef08a'; // kuning highlight
        setTimeout(() => {
          target.style.backgroundColor = prevBg || '';
          setTimeout(() => { target.style.transition = prevTrans || ''; }, 500);
        }, 1600);
      }
      setPendingHl(null);
    }, 220); // beri waktu render MarkdownView
    return () => clearTimeout(t);
  }, [pendingHl, selectedId, detailTab]);

  const load = useCallback(async () => {
    try {
      const data = await api('/api/notes');
      setNotes(data);
      setSelectedId(prev => (prev && data.some(n => n.id === prev)) ? prev : (data[0]?.id ?? null));
    } catch (e) { setAlert({ type: 'error', msg: e.message }); setNotes([]); }
  }, [api]);
  useEffect(() => { load(); }, [load]);

  // ── Helper konten tab aktif di editor ──
  // activeTab 0 = tab utama (editing.content); 1..n = editing.tabs[i-1].content
  const getActiveContent = (ed) =>
    !ed ? '' : (ed.activeTab === 0 ? ed.content : (ed.tabs?.[ed.activeTab - 1]?.content || ''));
  const updateActiveContent = (val) => setEditing(s => {
    if (!s) return s;
    if (s.activeTab === 0) return { ...s, content: val };
    const tabs = (s.tabs || []).slice();
    if (tabs[s.activeTab - 1]) tabs[s.activeTab - 1] = { ...tabs[s.activeTab - 1], content: val };
    return { ...s, tabs };
  });
  let _tabKeySeq = 0;
  const newTabKey = () => `t${Date.now()}_${_tabKeySeq++}`;

  const startNew  = () => { setShowPreview(false); setEditing({ id: null, title: '', content: '', scope: tab === 'common' ? 'common' : 'private', tabs: [], activeTab: 0 }); };
  const startEdit = (n) => {
    setShowPreview(false);
    setEditing({
      id: n.id, title: n.title, content: n.content, scope: n.scope || 'private',
      tabs: (n.tabs || []).map(t => ({ ...t, _k: newTabKey() })),
      activeTab: Math.min(Math.max(0, detailTab), (n.tabs || []).length),
    });
  };
  const selectNote = (n) => { setEditing(null); setSelectedId(n.id); setDetailTab(0); }; // eslint-disable-line

  const save = async () => {
    const e = editing;
    const hasTabContent = (e.tabs || []).some(t => (t.title || '').trim() || (t.content || '').trim());
    if (!e.title.trim() && !e.content.trim() && !hasTabContent) { setAlert({ type: 'error', msg: t('notes.titleContentRequired') }); return; }
    setBusy(true);
    try {
      const tabs = (e.tabs || []).map(t => ({ title: String(t.title || ''), content: String(t.content || '') }));
      const payload = { title: e.title, content: e.content, scope: e.scope || 'private', tabs };
      if (e.id) await api(`/api/notes/${e.id}`, { method: 'PUT',  body: JSON.stringify(payload) });
      else      await api('/api/notes',         { method: 'POST', body: JSON.stringify(payload) });
      setTab(e.scope === 'common' ? 'common' : 'private');
      if (e.id) setSelectedId(e.id);
      setEditing(null); setAlert({ type: 'success', msg: t('notes.saved') }); await load();
    } catch (err) { setAlert({ type: 'error', msg: err.message }); }
    finally { setBusy(false); }
  };

  const del = async (n) => {
    if (!window.confirm((t('notes.deleteNote')) + (n.title || (t('notes.untitled'))) + '"?')) return;
    try {
      await api(`/api/notes/${n.id}`, { method: 'DELETE' });
      if (editing && editing.id === n.id) setEditing(null);
      setAlert({ type: 'success', msg: t('notes.deleted') });
      await load();
    } catch (e) { setAlert({ type: 'error', msg: e.message }); }
  };

  // ── Search / Replace helpers ────────────────────────────────────────────
  const openSR = useCallback(() => {
    setShowSR(true);
    setSrMatchIdx(-1);
    setSrCount(null);
  }, []);

  const closeSR = useCallback(() => {
    setShowSR(false);
    setSrFind('');
    setSrReplace('');
    setSrMatchIdx(-1);
    setSrCount(null);
  }, []);

  const srFindNext = useCallback(() => {
    if (!srFind || !editing) return;
    const content = getActiveContent(editing);
    const needle  = srFind;
    const lower   = content.toLowerCase();
    const nLower  = needle.toLowerCase();
    // kumpulkan semua indeks
    const indices = [];
    let pos = 0;
    while ((pos = lower.indexOf(nLower, pos)) !== -1) { indices.push(pos); pos += nLower.length; }
    setSrCount(indices.length);
    if (indices.length === 0) { setSrMatchIdx(-1); return; }
    const next = (srMatchIdx + 1) % indices.length;
    setSrMatchIdx(next);
    const ta = textareaRef.current;
    if (ta) {
      ta.focus();
      ta.setSelectionRange(indices[next], indices[next] + needle.length);
      // scroll textarea ke posisi selection
      const linesBefore = content.slice(0, indices[next]).split('\n').length;
      const lineHeight = 18; // approx
      ta.scrollTop = Math.max(0, (linesBefore - 3) * lineHeight);
    }
  }, [srFind, srMatchIdx, editing]);

  const srReplaceOne = useCallback(() => {
    if (!srFind || !editing) return;
    const ta = textareaRef.current;
    if (!ta) return;
    const { selectionStart, selectionEnd } = ta;
    const cur = getActiveContent(editing);
    const sel = cur.slice(selectionStart, selectionEnd);
    if (sel.toLowerCase() === srFind.toLowerCase()) {
      const next = cur.slice(0, selectionStart) + srReplace + cur.slice(selectionEnd);
      updateActiveContent(next);
      setSrMatchIdx(-1);
      setSrCount(null);
    } else {
      srFindNext();
    }
  }, [srFind, srReplace, editing, srFindNext]);

  const srReplaceAll = useCallback(() => {
    if (!srFind || !editing) return;
    const regex = new RegExp(srFind.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi');
    const cur = getActiveContent(editing);
    const next = cur.replace(regex, srReplace);
    const count = (cur.match(regex) || []).length;
    updateActiveContent(next);
    setSrCount(count);
    setSrMatchIdx(-1);
  }, [srFind, srReplace, editing]);

  // ── Insert sintaks markdown di posisi kursor ────────────────────────────
  const insertAtCursor = useCallback((before, after = '', placeholder = '') => {
    const ta = textareaRef.current;
    if (!ta) return;
    ta.focus();
    const { selectionStart: ss, selectionEnd: se } = ta;
    const cur = getActiveContent(editing);
    const sel = cur.slice(ss, se) || placeholder;
    const next = cur.slice(0, ss) + before + sel + after + cur.slice(se);
    updateActiveContent(next);
    // Kembalikan fokus & set kursor setelah render
    setTimeout(() => {
      ta.focus();
      const newPos = ss + before.length + sel.length + after.length;
      ta.setSelectionRange(newPos, newPos);
    }, 0);
  }, [editing]);

  // Data cheatsheet — [label, before, after, placeholder, contoh tampil]
  const MD_HELP = [
    { group: t('notes.text'), items: [
      { label: 'Bold',        before: '**', after: '**', ph: 'teks tebal',   preview: '**tebal**' },
      { label: 'Italic',      before: '*',  after: '*',  ph: 'teks miring',  preview: '*miring*' },
      { label: 'Code inline', before: '`',  after: '`',  ph: 'kode',         preview: '`kode`' },
      { label: 'Bold+Italic', before: '***',after: '***',ph: 'teks',         preview: '***teks***' },
    ]},
    { group: id ? 'Heading' : 'Heading', items: [
      { label: 'H1', before: '# ',    after: '', ph: 'Judul 1', preview: '# H1' },
      { label: 'H2', before: '## ',   after: '', ph: 'Judul 2', preview: '## H2' },
      { label: 'H3', before: '### ',  after: '', ph: 'Judul 3', preview: '### H3' },
    ]},
    { group: id ? 'List' : 'List', items: [
      { label: t('notes.bullet'),   before: '- ',   after: '', ph: 'item', preview: '- item' },
      { label: t('notes.numbered'), before: '1. ',  after: '', ph: 'item', preview: '1. item' },
      { label: 'Checkbox',                      before: '- [ ] ',after:'', ph: 'task', preview: '- [ ] task' },
    ]},
    { group: t('notes.block'), items: [
      { label: t('notes.blockquote'), before: '> ',    after: '',    ph: 'kutipan',    preview: '> teks' },
      { label: t('notes.codeBlock'), before: '```\n', after: '\n```',ph: 'kode di sini', preview: '```…```' },
      { label: t('notes.svgBlock'),  before: '```svg\n', after: '\n```', ph: '<svg>…</svg>', preview: '```svg…```' },
      { label: t('notes.hr'),          before: '\n---\n',after: '',   ph: '',           preview: '---' },
    ]},
    { group: 'Link & Tabel', items: [
      { label: 'Link',  before: '[',  after: '](url)', ph: 'teks link', preview: '[teks](url)' },
      { label: t('notes.table'),
        before: '| Kolom 1 | Kolom 2 |\n|---------|----------|\n| ',
        after:  ' | nilai |\n',
        ph: 'nilai', preview: '| … |' },
    ]},
  ];
  const moveNote = async (n) => {
    const newScope = n.scope === 'common' ? 'private' : 'common';
    const label = newScope === 'common'
      ? (t('notes.moveCommon'))
      : (t('notes.moveMyNotes'));
    if (!window.confirm(label)) return;
    try {
      await api(`/api/notes/${n.id}`, { method: 'PUT', body: JSON.stringify({ title: n.title, content: n.content, scope: newScope }) });
      setAlert({ type: 'success', msg: t('notes.noteMoved') });
      setSelectedId(null);
      setTab(newScope === 'common' ? 'common' : 'private');
      await load();
    } catch (e) { setAlert({ type: 'error', msg: e.message }); }
  };

  // ── Reorder tab: helper generik pindah elemen array dari index `from` ke `to` ──
  const reorderArray = (arr, from, to) => {
    const copy = arr.slice();
    const [moved] = copy.splice(from, 1);
    copy.splice(to, 0, moved);
    return copy;
  };

  // Persist reorder tab di MODE BACA — langsung PUT ke DB (payload lengkap agar
  // tidak menimpa title/content/scope dengan nilai kosong). Optimistic update +
  // rollback bila gagal.
  const persistTabReorder = async (note, from, to) => {
    if (from === to) return;
    const prevTabs = note.tabs || [];
    const newTabs  = reorderArray(prevTabs, from, to);
    // Optimistic update ke state notes supaya UI langsung berubah.
    setNotes(prev => prev.map(n => (n.id === note.id ? { ...n, tabs: newTabs } : n)));
    // Ikuti tab yang sedang dibuka bila dia yang dipindah, atau ikut ter-shift.
    setDetailTab(dt => {
      if (dt === from + 1) return to + 1;
      if (from < to && dt > from && dt <= to + 1) return dt - 1;
      if (from > to && dt >= to + 1 && dt < from + 1) return dt + 1;
      return dt;
    });
    setReordering(true);
    try {
      const payload = {
        title: note.title, content: note.content, scope: note.scope,
        tabs: newTabs.map(t => ({ title: String(t.title || ''), content: String(t.content || '') })),
      };
      await api(`/api/notes/${note.id}`, { method: 'PUT', body: JSON.stringify(payload) });
    } catch (e) {
      // Rollback bila gagal simpan.
      setNotes(prev => prev.map(n => (n.id === note.id ? { ...n, tabs: prevTabs } : n)));
      setAlert({ type: 'error', msg: e.message });
    } finally {
      setReordering(false);
    }
  };


  // ── Preview search: hitung mark setelah React render, navigasi style-only ─
  useEffect(() => {
    const root = contentRef.current;
    if (!root) return;
    const marks = Array.from(root.querySelectorAll('mark[data-pvhl]'));
    const total = marks.length;
    setPvTotal(total);
    if (!total) { setPvMatchIdx(0); return; }
    const cur = 0;
    setPvMatchIdx(cur);
    marks.forEach((m, i) => {
      m.style.background = i === cur ? '#f97316' : '#fef08a';
      m.style.color       = i === cur ? '#fff'    : '#0f172a';
    });
    if (marks[cur]) marks[cur].scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [pvSearch, selectedId, detailTab]);

  const pvNav = useCallback((dir) => {
    const root = contentRef.current;
    if (!root) return;
    const marks = Array.from(root.querySelectorAll('mark[data-pvhl]'));
    if (!marks.length) return;
    const next = (pvMatchIdx + dir + marks.length) % marks.length;
    setPvMatchIdx(next);
    marks.forEach((m, i) => {
      m.style.background = i === next ? '#f97316' : '#fef08a';
      m.style.color       = i === next ? '#fff'    : '#0f172a';
    });
    marks[next].scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [pvMatchIdx]);

  // Reset preview search saat note berganti
  useEffect(() => { setPvSearch(''); setPvMatchIdx(0); setPvTotal(0); setPvTabHits([]); }, [selectedId]);

  // ── [Multi-tab search] "Cari di catatan" dulu cuma menghitung <mark> di TAB
  // AKTIF (via DOM) — kalau term ada di tab LAIN, hasilnya "Tidak ditemukan"
  // padahal sebenarnya ada, cuma di tab berbeda. Sekarang: cek SELURUH tab
  // (title+content mentah, bukan DOM), auto-pindah ke tab pertama yang match
  // bila tab aktif kosong, dan simpan daftar tab lain yg juga match (buat
  // chip "juga ada di: ..." supaya user bisa lompat manual). ──
  useEffect(() => {
    const term = pvSearch.trim().toLowerCase();
    if (!term) { setPvTabHits([]); return; }
    const note = notes.find(n => n.id === selectedId);
    if (!note) return;
    const segs = [
      { idx: 0, label: note.title || (t('notes.main')), text: `${note.title || ''}\n${note.content || ''}` },
      ...(note.tabs || []).map((t, i) => ({ idx: i + 1, label: t.title || `Tab ${i + 1}`, text: `${t.title || ''}\n${t.content || ''}` })),
    ];
    const matching = segs.filter(s => s.text.toLowerCase().includes(term));
    const activeMatches = matching.some(s => s.idx === detailTab);
    if (!activeMatches && matching.length) {
      setDetailTab(matching[0].idx);   // pindah otomatis ke tab pertama yg match
    }
    setPvTabHits(matching);   // simpan SEMUA tab yg match; difilter dari tab aktif saat render
    // sengaja TIDAK depend `detailTab` — cegah loop saat efek ini sendiri memanggil setDetailTab.
    // eslint-disable-next-line
  }, [pvSearch, selectedId, notes]);

  // Ctrl+Shift+F → fokus ke preview search (hanya saat tidak edit)
  useEffect(() => {
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'F') {
        if (editing) return; // jangan aktif saat mode edit
        e.preventDefault();
        pvSearchRef.current?.focus();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [editing]);

  if (notes === null) return <Loading />;

  const selected = notes.find(n => n.id === selectedId && (tab === 'common' ? n.scope === 'common' : n.scope !== 'common')) || null;

  const inTab = notes.filter(n => (tab === 'common' ? n.scope === 'common' : n.scope !== 'common'));

  // ── Search v2: multi-kata (AND), ikut mencari isi TABS, ranking judul dulu,
  //    plus metadata match (tab ke-berapa + cuplikan konteks) untuk ditampilkan
  //    di daftar dan dipakai lompat-highlight saat hasil diklik. ──
  const terms = q.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const searchNote = (n) => {
    if (!terms.length) return { ok: true, rank: 0, tabIdx: 0, snippet: '' };
    // Segmen yang bisa dicari: [tabIdx, label, teks]
    const segs = [[0, n.title || '', n.title || ''], [0, '', n.content || '']];
    (n.tabs || []).forEach((tb, i) => {
      segs.push([i + 1, tb.title || '', tb.title || '']);
      segs.push([i + 1, '', tb.content || '']);
    });
    // Semua term harus muncul di note (gabungan seluruh segmen).
    const all = segs.map(s => s[2]).join('\n').toLowerCase();
    if (!terms.every(t => all.includes(t))) return { ok: false };
    // Cari segmen pertama yang memuat term pertama → untuk snippet + jump.
    const t0 = terms[0];
    let tabIdx = 0, snippet = '', rank = 2;
    if ((n.title || '').toLowerCase().includes(t0)) rank = 0;          // match di judul note
    else if ((n.tabs || []).some(tb => (tb.title || '').toLowerCase().includes(t0))) rank = 1; // judul tab
    for (const [ti, , text] of segs) {
      const low = text.toLowerCase();
      const idx = low.indexOf(t0);
      if (idx !== -1) {
        tabIdx = ti;
        const from = Math.max(0, idx - 32);
        snippet = (from > 0 ? '…' : '') +
          text.slice(from, idx + t0.length + 48).replace(/\s+/g, ' ').trim() +
          (idx + t0.length + 48 < text.length ? '…' : '');
        break;
      }
    }
    return { ok: true, rank, tabIdx, snippet };
  };

  const withMatch = inTab
    .map(n => ({ n, m: searchNote(n) }))
    .filter(x => x.m.ok);
  const ql = terms.length > 0;

  const sorted = [...withMatch].sort((a, b) => {
    if (ql && a.m.rank !== b.m.rank) return a.m.rank - b.m.rank; // judul > judul tab > isi
    if (sortBy === 'title_asc')  return (a.n.title || '').localeCompare(b.n.title || '');
    if (sortBy === 'title_desc') return (b.n.title || '').localeCompare(a.n.title || '');
    if (sortBy === 'updated_asc')  return (a.n.updatedAt || '').localeCompare(b.n.updatedAt || '');
    return (b.n.updatedAt || '').localeCompare(a.n.updatedAt || ''); // updated_desc (default)
  });
  const filtered = withMatch; // kompatibilitas: dipakai untuk cek "tidak ada hasil"

  // -- Mass download --
  const visibleIds = sorted.map(({ n }) => n.id);
  const allVisibleSelected = visibleIds.length > 0 && visibleIds.every(x => selIds.has(x));
  const toggleSel = (nid) => setSelIds(prev => {
    const s = new Set(prev);
    if (s.has(nid)) s.delete(nid); else s.add(nid);
    return s;
  });
  const toggleSelectAll = () => setSelIds(prev => {
    const s = new Set(prev);
    if (allVisibleSelected) visibleIds.forEach(x => s.delete(x));
    else visibleIds.forEach(x => s.add(x));
    return s;
  });
  const exitSelMode = () => { setSelMode(false); setSelIds(new Set()); };
  const downloadSelected = async () => {
    const picked = (notes || []).filter(n => selIds.has(n.id));
    if (!picked.length) { setAlert({ type: 'error', msg: id ? 'Belum ada catatan yang dipilih.' : 'No notes selected.' }); return; }
    setZipBusy(true);
    try {
      const r = await downloadNotesZip(picked, { prefix: 'notes' });
      setAlert({ type: 'success', msg: id
        ? (picked.length + ' catatan diunduh (' + r.files + ' berkas .md) - ' + r.name)
        : (picked.length + ' notes downloaded (' + r.files + ' .md files) - ' + r.name) });
      exitSelMode();
    } catch (e) {
      setAlert({ type: 'error', msg: (id ? 'Gagal membuat ZIP: ' : 'Failed to build ZIP: ') + (e?.message || e) });
    } finally { setZipBusy(false); }
  };

  // Klik hasil saat search aktif → buka note, pindah ke tab match, highlight term.
  const openSearchResult = (n, m) => {
    setEditing(null);
    setShowPreview(false);
    setSelectedId(n.id);
    if (ql) {
      setDetailTab(m.tabIdx || 0);
      setPendingHl(m.snippet || terms[0]);
    } else {
      setDetailTab(0);
    }
  };

  const listItemStyle = (active) => ({
    display: 'block', width: '100%', textAlign: 'left', cursor: 'pointer',
    border: '1px solid', borderColor: active ? '#bfdbfe' : '#e2e8f0',
    background: active ? 'rgb(var(--brand-50))' : '#fff', borderRadius: 10,
    padding: '10px 12px', marginBottom: 8, transition: 'all 0.15s', fontFamily: 'inherit',
  });
  const paneCard = { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: '14px 16px' };

  const doDemote = async (mode) => {
    if (!demote) return;
    setDemoteBusy(true);
    try {
      const r = await api(`/api/notes/${demote.id}/demote`, { method: 'POST', body: JSON.stringify({ mode }) });
      setAlert({ type: 'success', msg: r.message || 'OK' });
      setDemote(null);
      if (mode === 'move') { setSelectedId(null); setDetailTab(0); }
      await load();
    } catch (e2) { setAlert({ type: 'error', msg: e2.message }); }
    finally { setDemoteBusy(false); }
  };

  return (
    <div>
      {shareNote && (
        <ShareModal note={shareNote} id={id} onClose={() => setShareNote(null)} api={api} />
      )}
      {demote && (
        <div onClick={e => { if (e.target === e.currentTarget) setDemote(null); }}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div style={{ background: '#fff', borderRadius: 14, padding: '20px 22px', width: '100%', maxWidth: 440, boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}>
            <div style={{ fontWeight: 700, fontSize: 14, color: '#0f172a', marginBottom: 4 }}>
              📤 {t('notes.notesPlain')}
            </div>
            <div style={{ fontSize: 12, color: '#64748b', marginBottom: 12, wordBreak: 'break-word' }}>
              {demote.title || (t('notes.untitled'))}
              {(demote.tabs?.length || 0) > 0 && ` · ${demote.tabs.length} tab${demote.tabs.length > 1 ? 's' : ''}`}
            </div>
            <div style={{ fontSize: 12.5, color: '#475569', lineHeight: 1.55, marginBottom: 14 }}>
              {t('notes.noteIncludingAllTabsWill')}
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
              <Btn onClick={() => setDemote(null)} disabled={demoteBusy}>{t('notes.cancel')}</Btn>
              <Btn onClick={() => doDemote('copy')} disabled={demoteBusy}>
                {demoteBusy ? '⏳' : '📋'} {t('notes.copyPlain')}
              </Btn>
              <Btn variant="danger" onClick={() => doDemote('move')}
                disabled={demoteBusy || (demote.scope === 'common' && !isAdmin)}
                title={demote.scope === 'common' && !isAdmin ? (t('notes.onlyAdminCanMoveShared')) : undefined}>
                {demoteBusy ? '⏳' : '📤'} {t('notes.moveRemoveRag')}
              </Btn>
            </div>
          </div>
        </div>
      )}
      {ctxMenu && (
        <div
          onClick={() => setCtxMenu(null)}
          onContextMenu={(e) => { e.preventDefault(); setCtxMenu(null); }}
          style={{ position: 'fixed', inset: 0, zIndex: 1100 }}>
          <div style={{
            position: 'fixed',
            left: Math.min(ctxMenu.x, window.innerWidth - 230),
            top:  Math.min(ctxMenu.y, window.innerHeight - 110),
            background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10,
            boxShadow: '0 8px 24px rgba(0,0,0,0.14)', padding: 4, minWidth: 210,
          }}>
            <button
              onClick={() => {
                window.open(`${window.location.origin}/note/${ctxMenu.note.id}`, '_blank', 'noopener');
                setCtxMenu(null);
              }}
              style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: 12.5, color: '#0f172a', padding: '8px 10px', borderRadius: 7 }}
              onMouseEnter={e => { e.currentTarget.style.background = '#f1f5f9'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'none'; }}>
              🗗 {t('notes.openNewTabCleanView')}
            </button>
            <button
              onClick={() => {
                const url = `${window.location.origin}/note/${ctxMenu.note.id}`;
                (navigator.clipboard?.writeText(url) || Promise.reject()).then(
                  () => setAlert({ type: 'success', msg: t('notes.linkCopied') }),
                  () => setAlert({ type: 'error', msg: t('notes.failedCopyLink') })
                );
                setCtxMenu(null);
              }}
              style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: 12.5, color: '#0f172a', padding: '8px 10px', borderRadius: 7 }}
              onMouseEnter={e => { e.currentTarget.style.background = '#f1f5f9'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'none'; }}>
              📋 {t('notes.copyLink')}
            </button>
          </div>
        </div>
      )}
      {impFile && (
        <ImportNotesZipModal
          file={impFile}
          endpoint="/api/notes"
          api={api}
          id={id}
          targetLabel="Notes"
          onClose={() => setImpFile(null)}
          onDone={(r) => {
            load();
            if (!r.keepOpen) {
              setImpFile(null);
              setAlert({ type: 'success', msg: id ? (r.ok + ' catatan diimpor.') : (r.ok + ' notes imported.') });
            }
          }} />
      )}
      <Alert msg={alert?.msg} type={alert?.type} onClose={() => setAlert(null)} />
      <div className="flex flex-col md:flex-row gap-2 items-start">
        {/* Kiri: daftar judul — mobile: full width & tanpa lebar dipaksa; sembunyi saat detail/editor terbuka */}
        <div
          className={isMobile ? ((selected || editing) ? 'hidden' : 'w-full') : ''}
          style={isMobile ? undefined : { display: 'flex', flexDirection: 'column', flexShrink: 0, width: leftOpen ? leftW : 36, transition: dragging ? 'none' : 'width 0.2s ease', minWidth: leftOpen ? leftW : 36 }}>
          {/* Tombol toggle */}
          <div style={{ display: isMobile ? 'none' : 'flex', justifyContent: leftOpen ? 'flex-end' : 'center', marginBottom: leftOpen ? 6 : 0 }}>
            <button
              onClick={() => setLeftOpen(o => !o)}
              title={leftOpen ? (t('notes.hideList')) : (t('notes.showList'))}
              style={{ background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 8, width: 28, height: 28, cursor: 'pointer', fontSize: 13, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748b', flexShrink: 0 }}
            >
              {leftOpen ? '◀' : '▶'}
            </button>
          </div>
          {leftOpen && <div>
          {/* Tab scope: Catatan Saya (private) vs Bersama (common) */}
          <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
            {[
              { k: 'private', label: t('notes.myNotes') },
              { k: 'common',  label: t('notes.common')   },
            ].map(tb => (
              <button key={tb.k}
                onClick={() => { setTab(tb.k); setEditing(null); setSelectedId(null); setQ(''); }}
                style={{
                  flex: 1, padding: '7px 8px', borderRadius: 8, cursor: 'pointer', fontFamily: 'inherit',
                  fontSize: 12, fontWeight: 600, transition: 'all .15s',
                  border: '1px solid', borderColor: tab === tb.k ? 'rgb(var(--brand-600))' : '#e2e8f0',
                  background: tab === tb.k ? 'rgb(var(--brand-50))' : '#fff', color: tab === tb.k ? 'rgb(var(--brand-700))' : '#64748b',
                }}>
                {tb.label}
              </button>
            ))}
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, gap: 8 }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              {t('notes.notes')}{inTab.length ? ` (${inTab.length})` : ''}
            </span>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              <input ref={zipRef} type="file" style={{ display: 'none' }}
                accept=".zip,application/zip,application/x-zip-compressed"
                onChange={e => { const f = e.target.files && e.target.files[0]; e.target.value = ''; if (f) setImpFile(f); }} />
              <Btn size="sm" onClick={() => zipRef.current && zipRef.current.click()}>
                {id ? '⬆ Impor ZIP' : '⬆ Import ZIP'}
              </Btn>
              <Btn size="sm" variant={selMode ? 'primary' : 'default'}
                onClick={() => (selMode ? exitSelMode() : setSelMode(true))}>
                {selMode ? (id ? '✖ Batal' : '✖ Cancel') : (id ? '☑ Pilih' : '☑ Select')}
              </Btn>
            <Btn size="sm" variant="primary" onClick={startNew}>➕ {t('notes.new')}</Btn>
            </div>
          </div>

          {inTab.length === 0 ? (
            <Empty icon="📝" text={t('notes.noNotesYet')} />
          ) : (
            <>
              <div style={{ position: 'relative', marginBottom: 10 }}>
                <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', fontSize: 13, color: '#94a3b8', pointerEvents: 'none' }}>🔍</span>
                <input
                  value={q}
                  onChange={e => setQ(e.target.value)}
                  placeholder={t('notes.searchTitleContent')}
                  style={{ ...inputStyle, paddingLeft: 30, paddingRight: q ? 28 : 10 }}
                />
                {q && (
                  <button
                    onClick={() => setQ('')}
                    title={t('notes.clear')}
                    style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: 16, lineHeight: 1 }}
                  >×</button>
                )}
              </div>

              {filtered.length === 0 ? (
                <div style={{ padding: '1.5rem 0.5rem', textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>
                  {t('notes.noMatchingNotes')}
                </div>
              ) : (
                <>
                  {/* Sort control */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                    <span style={{ fontSize: 11, color: '#94a3b8', whiteSpace: 'nowrap' }}>
                      {t('notes.sort')}
                    </span>
                    <select
                      value={sortBy}
                      onChange={e => setSortBy(e.target.value)}
                      style={{ flex: 1, fontSize: 11.5, padding: '4px 6px', border: '1px solid #e2e8f0', borderRadius: 6, background: '#fff', color: '#475569', cursor: 'pointer', outline: 'none', fontFamily: 'inherit' }}
                    >
                      <option value="updated_desc">{t('notes.lastModified')}</option>
                      <option value="updated_asc">{t('notes.oldestModified')}</option>
                      <option value="title_asc">{t('notes.titleZ')}</option>
                      <option value="title_desc">{t('notes.titleZ2')}</option>
                    </select>
                  </div>
                  {selMode && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px', marginBottom: 6,
                      background: 'rgb(var(--brand-50))', border: '1px solid #bfdbfe', borderRadius: 8 }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11.5, fontWeight: 700, color: '#475569', cursor: 'pointer' }}>
                        <input type="checkbox" checked={allVisibleSelected} onChange={toggleSelectAll} style={{ width: 14, height: 14 }} />
                        {(id ? 'Pilih semua' : 'Select all') + ' (' + visibleIds.length + ')'}
                      </label>
                      <span style={{ marginLeft: 'auto', fontSize: 11, color: '#64748b' }}>
                        {selIds.size + (id ? ' dipilih' : ' selected')}
                      </span>
                      <Btn size="sm" variant="primary" onClick={downloadSelected} disabled={zipBusy || selIds.size === 0}>
                        {(zipBusy ? '⏳ ' : '') + (id ? 'Unduh ZIP' : 'Download ZIP')}
                      </Btn>
                    </div>
                  )}
                  <div style={{ maxHeight: '60vh', overflowY: 'auto', paddingRight: 2 }}>
                    {sorted.map(({ n, m }) => {
                      const active = !editing && n.id === selectedId;
                      // Highlight term pertama di dalam cuplikan hasil pencarian.
                      const renderSnippet = (txt) => {
                        if (!txt) return null;
                        const t0 = terms[0];
                        const idx = txt.toLowerCase().indexOf(t0);
                        if (idx === -1) return txt;
                        return (<>
                          {txt.slice(0, idx)}
                          <mark style={{ background: '#fef08a', borderRadius: 2, padding: '0 1px' }}>{txt.slice(idx, idx + t0.length)}</mark>
                          {txt.slice(idx + t0.length)}
                        </>);
                      };
                      return (
                        <div key={n.id} style={{ display: 'flex', alignItems: 'stretch', gap: 6 }}>
                          {selMode && (
                            <label style={{ display: 'flex', alignItems: 'center', paddingLeft: 2, cursor: 'pointer' }}>
                              <input type="checkbox" checked={selIds.has(n.id)} onChange={() => toggleSel(n.id)}
                                style={{ width: 14, height: 14 }} />
                            </label>
                          )}
                        <button onClick={() => (selMode ? toggleSel(n.id) : openSearchResult(n, m))}
                          onContextMenu={(e) => { e.preventDefault(); setCtxMenu({ x: e.clientX, y: e.clientY, note: n }); }}
                          style={{ ...listItemStyle(active), flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 600, color: active ? 'rgb(var(--brand-700))' : '#0f172a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {n.title || (t('notes.untitled'))}
                          </div>
                          {ql && m.snippet && (
                            <div style={{ fontSize: 11.5, color: '#475569', marginTop: 3, lineHeight: 1.45, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', wordBreak: 'break-word' }}>
                              {m.tabIdx > 0 && (
                                <span style={{ fontSize: 10, fontWeight: 700, color: '#7c3aed', background: '#f5f3ff', border: '1px solid #ddd6fe', borderRadius: 4, padding: '0 4px', marginRight: 5 }}>
                                  {(n.tabs || [])[m.tabIdx - 1]?.title || `Tab ${m.tabIdx}`}
                                </span>
                              )}
                              {renderSnippet(m.snippet)}
                            </div>
                          )}
                          <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {n.updatedBy ? `${t('notes.by')} ${n.updatedBy} · ` : ''}{n.updatedAt}
                          </div>
                        </button>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </>
          )}
          </div>}
        </div>

        {/* Divider drag-resize (desktop, saat panel kiri terbuka) */}
        {leftOpen && (
          <div onMouseDown={startDrag} onDoubleClick={() => { setLeftW(288); try { localStorage.setItem('notesLeftW', '288'); } catch (_) {} }}
            className="hidden md:flex self-stretch group"
            title={t('notes.dragResizeDoubleClickReset')}
            style={{ width: 10, cursor: 'col-resize', flexShrink: 0, justifyContent: 'center', minHeight: 120 }}>
            <div style={{ width: 3, borderRadius: 3, background: dragging ? '#60a5fa' : '#e2e8f0', transition: 'background 0.15s' }}
              className="group-hover:!bg-blue-300" />
          </div>
        )}

        {/* Kanan: detail / editor — mobile: overlay full-screen saat ada note dipilih/diedit */}
        <div
          className={isMobile
            ? ((selected || editing) ? 'fixed inset-0 z-[900] bg-white overflow-y-auto' : 'hidden')
            : 'flex-1 min-w-0 w-full'}
          style={isMobile && (selected || editing) ? { padding: '12px', paddingBottom: 'max(env(safe-area-inset-bottom), 16px)' } : undefined}>
          {isMobile && (selected || editing) && (
            <button onClick={() => { setEditing(null); setSelectedId(null); }}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginBottom: 10, padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: 10, background: '#fff', fontFamily: 'inherit', fontSize: 13, fontWeight: 600, color: '#475569', cursor: 'pointer' }}>
              ← {t('notes.back')}
            </button>
          )}
          {editing ? (
            <div style={paneCard}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', marginBottom: 12 }}>{editing.id ? (t('notes.editNote')) : (t('notes.newNote'))}</div>
              <Field label={t('notes.title')}><Input value={editing.title} onChange={e => setEditing(s => ({ ...s, title: e.target.value }))} placeholder={t('notes.noteTitle')} /></Field>
              <Field label={t('notes.visibility')}>
                <div style={{ display: 'flex', gap: 8 }}>
                  {[
                    { k: 'private', label: t('notes.privateOnlyMe') },
                    { k: 'common',  label: t('notes.commonAllUsers') },
                  ].map(o => (
                    <button key={o.k} type="button"
                      onClick={() => setEditing(s => ({ ...s, scope: o.k }))}
                      style={{
                        flex: 1, padding: '8px 10px', borderRadius: 8, cursor: 'pointer', fontFamily: 'inherit',
                        fontSize: 12.5, fontWeight: 600, transition: 'all .15s', border: '1px solid',
                        borderColor: editing.scope === o.k ? 'rgb(var(--brand-600))' : '#e2e8f0',
                        background: editing.scope === o.k ? 'rgb(var(--brand-50))' : '#fff',
                        color: editing.scope === o.k ? 'rgb(var(--brand-700))' : '#64748b',
                      }}>
                      {o.label}
                    </button>
                  ))}
                </div>
              </Field>
              <div style={{ marginBottom: 12 }}>
                {/* Tab bar editor */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap', marginBottom: 8, borderBottom: '1px solid #e2e8f0', paddingBottom: 8 }}>
                  {[{ title: editing.title || (t('notes.main')), main: true }, ...(editing.tabs || [])].map((t, idx) => {
                    const isActive = editing.activeTab === idx;
                    return (
                      <div
                        key={idx === 0 ? 'main' : (t._k || idx)}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 2,
                          outline: (idx > 0 && editOverIdx === idx - 1 && editDragIdx !== null && editDragIdx !== idx - 1) ? '2px dashed rgb(var(--brand-600))' : 'none',
                          outlineOffset: 2, opacity: (idx > 0 && editDragIdx === idx - 1) ? 0.4 : 1,
                        }}
                      >
                        <button
                          type="button"
                          draggable={idx > 0}
                          onDragStart={idx > 0 ? (e) => { setEditDragIdx(idx - 1); e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', String(idx - 1)); } : undefined}
                          onDragOver={idx > 0 ? (e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; if (editDragIdx !== null) setEditOverIdx(idx - 1); } : undefined}
                          onDragLeave={idx > 0 ? () => setEditOverIdx(o => (o === idx - 1 ? null : o)) : undefined}
                          onDrop={idx > 0 ? (e) => {
                            e.preventDefault();
                            const to = idx - 1;
                            setEditing(s => {
                              if (editDragIdx === null || editDragIdx === to) return s;
                              const tabs = reorderArray(s.tabs || [], editDragIdx, to);
                              const na = s.activeTab === editDragIdx + 1 ? to + 1 : s.activeTab;
                              return { ...s, tabs, activeTab: na };
                            });
                            setEditDragIdx(null); setEditOverIdx(null);
                          } : undefined}
                          onDragEnd={() => { setEditDragIdx(null); setEditOverIdx(null); }}
                          onClick={() => setEditing(s => ({ ...s, activeTab: idx }))}
                          style={{
                            display: 'inline-flex', alignItems: 'center', gap: 5,
                            padding: '5px 10px', borderRadius: '6px 6px 0 0', cursor: idx > 0 ? 'grab' : 'pointer', fontFamily: 'inherit',
                            fontSize: 12, fontWeight: 600, border: '1px solid', borderBottom: isActive ? '1px solid #fff' : '1px solid #e2e8f0',
                            borderColor: isActive ? 'rgb(var(--brand-600))' : '#e2e8f0',
                            background: isActive ? 'rgb(var(--brand-50))' : '#f8fafc',
                            color: isActive ? 'rgb(var(--brand-700))' : '#64748b',
                            marginBottom: -9, position: 'relative',
                            maxWidth: 180, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                          }}
                          title={idx === 0 ? (id ? 'Tab utama (judul note)' : 'Main tab (note title)') : (id ? `${t.title} — tahan & geser untuk pindah urutan` : `${t.title} — drag to reorder`)}
                        >
                          {idx > 0 && <span style={{ color: '#cbd5e1', fontSize: 11, letterSpacing: -1 }}>⠿</span>}
                          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {idx === 0 ? `📄 ${t.title}` : (t.title || (id ? `Tab ${idx}` : `Tab ${idx}`))}
                          </span>
                        </button>
                        {idx > 0 && (
                          <button type="button"
                            onClick={() => {
                              if (!window.confirm(id ? `Hapus tab "${t.title || 'Tab ' + idx}"?` : `Delete tab "${t.title || 'Tab ' + idx}"?`)) return;
                              setEditing(s => {
                                const tabs = (s.tabs || []).slice();
                                tabs.splice(idx - 1, 1);
                                const na = s.activeTab >= idx ? Math.max(0, s.activeTab - 1) : s.activeTab;
                                return { ...s, tabs, activeTab: Math.min(na, tabs.length) };
                              });
                            }}
                            title={id ? 'Hapus tab' : 'Delete tab'}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#cbd5e1', fontSize: 14, padding: '0 2px', lineHeight: 1 }}
                            onMouseEnter={e => e.currentTarget.style.color = '#dc2626'}
                            onMouseLeave={e => e.currentTarget.style.color = '#cbd5e1'}
                          >×</button>
                        )}
                      </div>
                    );
                  })}
                  <button type="button"
                    onClick={() => setEditing(s => {
                      const tabs = (s.tabs || []).slice();
                      const n = tabs.length + 1;
                      tabs.push({ title: id ? `Tab ${n}` : `Tab ${n}`, content: '', _k: newTabKey() });
                      return { ...s, tabs, activeTab: tabs.length };
                    })}
                    title={t('notes.addTab')}
                    style={{ padding: '4px 9px', borderRadius: 6, cursor: 'pointer', fontFamily: 'inherit', fontSize: 13, fontWeight: 700, border: '1px dashed #cbd5e1', background: '#fff', color: 'rgb(var(--brand-600))', marginBottom: -9 }}
                  >+</button>
                </div>
                {(editing.tabs || []).length > 0 && (
                  <div style={{ fontSize: 10.5, color: '#94a3b8', marginTop: -4, marginBottom: 8 }}>
                    💡 {t('notes.holdTabThenDragReorder')}
                  </div>
                )}
                {/* Rename tab aktif (kecuali tab utama yang ikut judul note) */}
                {editing.activeTab > 0 && (
                  <div style={{ marginBottom: 8 }}>
                    <Input
                      value={editing.tabs[editing.activeTab - 1]?.title || ''}
                      onChange={e => setEditing(s => {
                        const tabs = (s.tabs || []).slice();
                        if (tabs[s.activeTab - 1]) tabs[s.activeTab - 1] = { ...tabs[s.activeTab - 1], title: e.target.value };
                        return { ...s, tabs };
                      })}
                      placeholder={t('notes.tabTitle')}
                    />
                  </div>
                )}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                  <label style={{ fontSize: 12, color: '#64748b', fontWeight: 600 }}>{t('notes.contentMarkdownSupported')}</label>
                  <div style={{ display: 'flex', gap: 4 }}>
                    <Btn size="sm" variant={!showPreview ? 'primary' : 'default'} onClick={() => { setShowPreview(false); }}>✏️ Edit</Btn>
                    <Btn size="sm" variant={showPreview ? 'primary' : 'default'} onClick={() => setShowPreview(true)}>👁️ Preview</Btn>
                    {!showPreview && (
                      <Btn size="sm" variant={showSR ? 'primary' : 'default'} onClick={() => showSR ? closeSR() : openSR()} title="Ctrl+H">
                        🔍 {t('notes.findReplace')}
                      </Btn>
                    )}
                    {!showPreview && (
                      <Btn size="sm" variant={showHelp ? 'primary' : 'default'} onClick={() => setShowHelp(h => !h)} title={t('notes.markdownHelp')}>
                        ? {t('notes.help')}
                      </Btn>
                    )}
                  </div>
                </div>
                {showPreview ? (
                  <div style={{ border: '1px solid #e2e8f0', borderRadius: 8, padding: '12px 14px', minHeight: 200, background: '#fafafa' }}>
                    {getActiveContent(editing).trim()
                      ? <MarkdownView source={getActiveContent(editing)} />
                      : <span style={{ color: '#94a3b8', fontSize: 13 }}>{t('notes.noContentYet')}</span>}
                  </div>
                ) : (
                  <>
                    {/* Search / Replace Toolbar */}
                    {showSR && (
                      <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '8px 10px', marginBottom: 6, display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
                        <input
                          value={srFind} onChange={e => { setSrFind(e.target.value); setSrMatchIdx(-1); setSrCount(null); }}
                          placeholder={t('notes.find')}
                          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); srFindNext(); } if (e.key === 'Escape') closeSR(); }}
                          style={{ ...inputStyle, width: 140, fontSize: 12, padding: '5px 8px' }}
                          autoFocus
                        />
                        <input
                          value={srReplace} onChange={e => setSrReplace(e.target.value)}
                          placeholder={t('notes.replace')}
                          onKeyDown={e => { if (e.key === 'Escape') closeSR(); }}
                          style={{ ...inputStyle, width: 150, fontSize: 12, padding: '5px 8px' }}
                        />
                        <Btn size="sm" onClick={srFindNext} disabled={!srFind}>
                          {t('notes.find2')}
                        </Btn>
                        <Btn size="sm" onClick={srReplaceOne} disabled={!srFind}>
                          {t('notes.replace2')}
                        </Btn>
                        <Btn size="sm" variant="primary" onClick={srReplaceAll} disabled={!srFind}>
                          {t('notes.replaceAll')}
                        </Btn>
                        {srCount !== null && (
                          <span style={{ fontSize: 11.5, color: srCount === 0 ? '#dc2626' : '#16a34a', fontWeight: 600 }}>
                            {srCount === 0
                              ? (t('notes.notFound'))
                              : (id ? `${srCount} hasil` : `${srCount} match${srCount !== 1 ? 'es' : ''}`)}
                          </span>
                        )}
                        <button onClick={closeSR} style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: '#94a3b8', lineHeight: 1 }}>×</button>
                      </div>
                    )}
                    {/* Markdown Help Panel */}
                    {showHelp && (
                      <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '10px 12px', marginBottom: 6 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                          <span style={{ fontSize: 11.5, fontWeight: 700, color: '#475569' }}>
                            📖 {t('notes.markdownReferenceClickInsert')}
                          </span>
                          <button onClick={() => setShowHelp(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: 15, lineHeight: 1 }}>×</button>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                          {MD_HELP.map(group => (
                            <div key={group.group}>
                              <div style={{ fontSize: 10.5, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>{group.group}</div>
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                                {group.items.map(item => (
                                  <button
                                    key={item.label}
                                    onClick={() => insertAtCursor(item.before, item.after, item.ph)}
                                    title={item.preview}
                                    style={{
                                      background: '#fff', border: '1px solid #e2e8f0', borderRadius: 6,
                                      padding: '4px 8px', cursor: 'pointer', fontFamily: 'monospace',
                                      fontSize: 11.5, color: '#334155', display: 'flex', flexDirection: 'column',
                                      alignItems: 'flex-start', gap: 1, transition: 'all 0.1s',
                                    }}
                                    onMouseEnter={e => { e.currentTarget.style.background = 'rgb(var(--brand-50))'; e.currentTarget.style.borderColor = '#bfdbfe'; }}
                                    onMouseLeave={e => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.borderColor = '#e2e8f0'; }}
                                  >
                                    <span style={{ fontSize: 10, color: '#64748b', fontFamily: 'inherit', fontWeight: 600 }}>{item.label}</span>
                                    <span style={{ color: 'rgb(var(--brand-600))' }}>{item.preview}</span>
                                  </button>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                        <div style={{ marginTop: 8, fontSize: 10.5, color: '#94a3b8', borderTop: '1px solid #e2e8f0', paddingTop: 6 }}>
                          💡 {t('notes.selectTextEditorFirstThen')}
                        </div>
                      </div>
                    )}
                    <textarea
                      ref={textareaRef}
                      value={getActiveContent(editing)}
                      onChange={e => updateActiveContent(e.target.value)}
                      rows={16}
                      onKeyDown={e => {
                        if ((e.ctrlKey || e.metaKey) && e.key === 'h') { e.preventDefault(); openSR(); }
                        if (e.key === 'Escape' && showSR) closeSR();
                      }}
                      placeholder={"# Judul\n**tebal**, *miring*, `kode`\n- daftar\n1. bernomor\n> kutipan\n```\nblok kode\n```"}
                      style={{ ...inputStyle, resize: 'vertical', minHeight: 280, lineHeight: 1.5, fontFamily: 'monospace', fontSize: 12.5 }}
                    />
                  </>
                )}
                <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>
                  Markdown: heading (#), <strong>**tebal**</strong>, *miring*, `kode`, daftar (- atau 1.), &gt; kutipan, ``` blok kode ```, [link](url), --- garis, tabel (| kol | kol |).
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
                <Btn onClick={() => setEditing(null)}>{t('notes.cancel')}</Btn>
                <Btn variant="primary" disabled={busy} onClick={save}>{busy ? (t('notes.saving')) : (t('notes.save'))}</Btn>
              </div>
            </div>
          ) : selected ? (
            <div style={paneCard}>
              <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent: 'space-between', alignItems: isMobile ? 'stretch' : 'flex-start', gap: 10 }}>
                <div style={{ fontSize: isMobile ? 19 : 17, fontWeight: 700, color: '#0f172a', wordBreak: 'break-word' }}>{selected.title || (t('notes.untitled'))}</div>
                <div style={{ display: 'flex', gap: 6, flexShrink: 0, flexWrap: isMobile ? 'wrap' : 'nowrap' }}>
                  {isAdmin && (
                    <Btn size="sm" onClick={() => setShareNote(selected)}>🔗 {isMobile ? '' : (t('notes.share'))}</Btn>
                  )}
                  {canPlain && (
                    <Btn size="sm" onClick={() => setDemote(selected)} title={t('notes.copyMoveNotesPlainNon')}>
                      📤 {isMobile ? '' : (t('notes.plain'))}
                    </Btn>
                  )}
                  <Btn size="sm" onClick={() => moveNote(selected)} title={selected.scope === 'common' ? (t('notes.moveMyNotes2')) : (t('notes.moveCommon2'))}>
                    {selected.scope === 'common' ? '🔒' : '👥'} {isMobile ? '' : (t('notes.move'))}
                  </Btn>
                  <Btn size="sm" onClick={() => startEdit(selected)}>✏️ {isMobile ? '' : 'Edit'}</Btn>
                  {!(selected.scope === 'common' && !isAdmin) && (
                    <Btn size="sm" variant="danger" onClick={() => del(selected)}>🗑️</Btn>
                  )}
                </div>
              </div>
              <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4, marginBottom: 12 }}>
                {selected.updatedBy ? `${t('notes.by')} ${selected.updatedBy} · ` : ''}{selected.updatedAt}
              </div>
              <div ref={contentRef} style={{ borderTop: '1px solid #f1f5f9', paddingTop: 12 }}>
                {/* Tab bar mode baca */}
                {(selected.tabs && selected.tabs.length > 0) && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap', marginBottom: 12 }}>
                    {[{ title: selected.title || (t('notes.main')) }, ...selected.tabs].map((t, idx) => {
                      const isActive = detailTab === idx;
                      return (
                        <button key={idx} type="button"
                          draggable={idx > 0}
                          onDragStart={idx > 0 ? (e) => { setViewDragIdx(idx - 1); e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', String(idx - 1)); } : undefined}
                          onDragOver={idx > 0 ? (e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; if (viewDragIdx !== null) setViewOverIdx(idx - 1); } : undefined}
                          onDragLeave={idx > 0 ? () => setViewOverIdx(o => (o === idx - 1 ? null : o)) : undefined}
                          onDrop={idx > 0 ? (e) => {
                            e.preventDefault();
                            const to = idx - 1;
                            const from = viewDragIdx;
                            setViewDragIdx(null); setViewOverIdx(null);
                            if (from === null || from === to || reordering) return;
                            persistTabReorder(selected, from, to);
                          } : undefined}
                          onDragEnd={() => { setViewDragIdx(null); setViewOverIdx(null); }}
                          onClick={() => { setDetailTab(idx); setPvSearch(''); setPvMatchIdx(0); setPvTotal(0); }}
                          style={{
                            display: 'inline-flex', alignItems: 'center', gap: 5,
                            padding: '5px 12px', borderRadius: 8, cursor: idx > 0 ? 'grab' : 'pointer', fontFamily: 'inherit',
                            fontSize: 12, fontWeight: 600, border: '1px solid',
                            borderColor: isActive ? 'rgb(var(--brand-600))' : '#e2e8f0',
                            background: isActive ? 'rgb(var(--brand-50))' : '#fff',
                            color: isActive ? 'rgb(var(--brand-700))' : '#64748b',
                            maxWidth: 200, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                            outline: (idx > 0 && viewOverIdx === idx - 1 && viewDragIdx !== null && viewDragIdx !== idx - 1) ? '2px dashed rgb(var(--brand-600))' : 'none',
                            outlineOffset: 2, opacity: (idx > 0 && viewDragIdx === idx - 1) ? 0.4 : 1,
                          }}
                          title={idx === 0 ? t.title : (id ? `${t.title} — tahan & geser untuk pindah urutan` : `${t.title} — drag to reorder`)}
                        >
                          {idx > 0 && <span style={{ color: '#cbd5e1', fontSize: 11, letterSpacing: -1 }}>⠿</span>}
                          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {idx === 0 ? `📄 ${t.title}` : (t.title || `Tab ${idx}`)}
                          </span>
                        </button>
                      );
                    })}
                    {reordering && <span style={{ fontSize: 11, color: '#94a3b8' }}>⏳ {t('notes.savingOrder')}</span>}
                  </div>
                )}
                {(selected.tabs && selected.tabs.length > 0) && (
                  <div style={{ fontSize: 10.5, color: '#94a3b8', marginTop: -8, marginBottom: 10 }}>
                    💡 {t('notes.holdTabThenDragReorder2')}
                  </div>
                )}
                {/* Preview search bar */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
                  <div style={{ position: 'relative', flex: 1, maxWidth: 280 }}>
                    <span style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', fontSize: 12, color: '#94a3b8', pointerEvents: 'none' }}>🔍</span>
                    <input
                      ref={pvSearchRef}
                      value={pvSearch}
                      onChange={e => {
                        const v = e.target.value;
                        setPvSearch(v);
                        setPvMatchIdx(0);
                      }}
                      onKeyDown={e => {
                        if (e.key === 'Enter') { e.preventDefault(); pvNav(e.shiftKey ? -1 : 1); }
                        if (e.key === 'Escape') { setPvSearch(''); setPvMatchIdx(0); setPvTotal(0); }
                      }}
                      placeholder={t('notes.searchNote')}
                      style={{ ...inputStyle, paddingLeft: 28, paddingRight: pvSearch ? 28 : 10, fontSize: 12, padding: '5px 28px' }}
                    />
                    {pvSearch && (
                      <button onClick={() => { setPvSearch(''); setPvMatchIdx(0); setPvTotal(0); }}
                        style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: 15, lineHeight: 1 }}>×</button>
                    )}
                  </div>
                  {pvSearch && (
                    <>
                      <span style={{ fontSize: 11.5, color: pvTotal === 0 ? '#dc2626' : '#64748b', fontWeight: 600, whiteSpace: 'nowrap' }}>
                        {pvTotal === 0 ? (t('notes.notFound')) : `${pvMatchIdx + 1}/${pvTotal}`}
                      </span>
                      <button onClick={() => pvNav(-1)} disabled={pvTotal === 0} style={{ background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 6, width: 24, height: 24, cursor: pvTotal ? 'pointer' : 'not-allowed', fontSize: 11, display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: pvTotal ? 1 : 0.4 }}>▲</button>
                      <button onClick={() => pvNav(1)}  disabled={pvTotal === 0} style={{ background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 6, width: 24, height: 24, cursor: pvTotal ? 'pointer' : 'not-allowed', fontSize: 11, display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: pvTotal ? 1 : 0.4 }}>▼</button>
                    </>
                  )}
                </div>
                {pvSearch && pvTabHits.filter(h => h.idx !== detailTab).length > 0 && (
                  <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 5, marginBottom: 10, marginTop: -4 }}>
                    <span style={{ fontSize: 11, color: '#94a3b8' }}>{t('notes.alsoFound')}</span>
                    {pvTabHits.filter(h => h.idx !== detailTab).map(h => (
                      <button key={h.idx} onClick={() => setDetailTab(h.idx)}
                        style={{ fontSize: 11, fontWeight: 600, color: '#7c3aed', background: '#f5f3ff', border: '1px solid #ddd6fe', borderRadius: 999, padding: '2px 9px', cursor: 'pointer', fontFamily: 'inherit', maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {h.label}
                      </button>
                    ))}
                  </div>
                )}
                {(() => {
                  const detailContent = detailTab === 0 ? selected.content : (selected.tabs?.[detailTab - 1]?.content || '');
                  // ── TOC otomatis dari heading (# .. ###) — klik → scroll ke bagian. ──
                  const headings = [];
                  let inCode = false;
                  String(detailContent || '').split('\n').forEach(ln => {
                    if (/^```/.test(ln.trim())) { inCode = !inCode; return; }
                    if (inCode) return;
                    const m = ln.match(/^(#{1,3})\s+(.+)$/);
                    if (m) headings.push({ level: m[1].length, text: m[2].trim() });
                  });
                  const jumpTo = (txt) => {
                    const root = contentRef.current;
                    if (!root) return;
                    const norm = (s) => String(s).toLowerCase().replace(/\s+/g, ' ').trim();
                    const target = [...root.querySelectorAll('h1, h2, h3')]
                      .find(el => norm(el.textContent).startsWith(norm(txt).slice(0, 40)));
                    if (target) {
                      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      const prev = target.style.backgroundColor;
                      target.style.transition = 'background-color 0.4s';
                      target.style.backgroundColor = '#fef08a';
                      setTimeout(() => { target.style.backgroundColor = prev || ''; }, 1200);
                    }
                  };
                  return (
                    <>
                      {headings.length >= 2 && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 12, padding: '8px 10px', background: '#f8fafc', border: '1px solid #f1f5f9', borderRadius: 8 }}>
                          <span style={{ fontSize: 10.5, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', alignSelf: 'center', marginRight: 4 }}>
                            {t('notes.outline')}
                          </span>
                          {headings.map((h, hi) => (
                            <button key={hi} onClick={() => jumpTo(h.text)}
                              style={{
                                background: '#fff', border: '1px solid #e2e8f0', borderRadius: 999, cursor: 'pointer',
                                fontFamily: 'inherit', fontSize: 11, fontWeight: h.level === 1 ? 700 : 500,
                                color: h.level === 1 ? '#0f172a' : '#475569',
                                padding: '3px 10px', marginLeft: h.level === 3 ? 10 : h.level === 2 ? 4 : 0,
                                maxWidth: 220, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                              }}
                              title={h.text}>
                              {h.text}
                            </button>
                          ))}
                        </div>
                      )}
                      {detailContent
                        ? <MarkdownView source={detailContent} highlight={pvSearch || undefined} />
                        : <span style={{ color: '#94a3b8', fontSize: 13 }}>{t('notes.noContent')}</span>}
                    </>
                  );
                })()}
              </div>
            </div>
          ) : (
            <Empty icon="🗒️" text={t('notes.selectNoteLeftCreateNew')} />
          )}
        </div>
      </div>
    </div>
  );
}

// ── Halaman ─────────────────────────────────────────────────────────────────
export default function Notes() {
  const api = useApi();
  const {
    lang,
    t
  } = useLang();
  const id = lang !== 'en';

  return (
    <div style={{ padding: '1.5rem', fontFamily: "'Inter', 'Segoe UI', system-ui, sans-serif" }}>
      {/* Page Header */}
      <div style={{ marginBottom: '1.5rem' }}>
        <h2 style={{ fontSize: 20, fontWeight: 700, color: '#0f172a', margin: 0 }}>📝 {t('notes.notes')}</h2>
        <p style={{ fontSize: 13, color: '#64748b', marginTop: 4, marginBottom: 0 }}>
          {t('notes.privateNotesSharedTeamNotes')}
        </p>
      </div>
      {/* Content */}
      <div style={{ background: '#fff', borderRadius: 12, padding: '1.25rem', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
        <NotesBoard api={api} id={id} />
      </div>
    </div>
  );
}
